import { useRef } from 'react';
import { Button, ButtonProps } from '@shared/components/Button';
import { useIsDesktop } from '@shared/util/hooks';
import { HotKey, useHotkeyBinding } from '@shared/util/hotkeys';
import { i18n } from '@shared/util/i18n';
import { isInTopmostStackedDialog } from './stack';

type ModalNavButtonProps = Pick<
  ButtonProps,
  'onClick' | 'testID' | 'tooltip' | 'tooltipPosition' | 'tooltipHotkey' | 'tooltipDisabled'
>;

export function ModalCloseButton({
  onClick,
  testID = 'modal-close-button',
  ...tooltipProps
}: ModalNavButtonProps) {
  return (
    <Button
      icon="exit"
      iconOnly
      iconGridSize={3.75}
      variant="ghost"
      onClick={onClick}
      testID={testID}
      {...tooltipProps}
    />
  );
}

export function ModalBackButton({
  onClick,
  testID = 'modal-back-button',
  ...tooltipProps
}: ModalNavButtonProps) {
  return (
    <Button
      icon="arrow-point-left"
      iconOnly
      iconGridSize={3.75}
      variant="ghost"
      onClick={onClick}
      testID={testID}
      {...tooltipProps}
    />
  );
}

export function ModalResizeButton({
  onClick,
  testID = 'modal-resize-button',
  isExpanded,
  className,
}: ButtonProps & { isExpanded: boolean; onClick: VoidFunction }) {
  const isDesktop = useIsDesktop();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const toggleFixedWidthHotkey = useHotkeyBinding('toggleFixedWidth');

  // Every open ResizeButton registers the same chord; only the topmost stacked
  // dialog (card modal vs Ask, etc.) should act — same rule Escape uses.
  const onHotkey = () => {
    if (!isInTopmostStackedDialog(buttonRef.current)) {
      return;
    }
    onClick();
  };

  if (!isDesktop) {
    return null;
  }

  const icon = isExpanded ? 'arrows-collapse' : 'arrows-expand';
  const resizeTooltip = isExpanded ? i18n('common.shrink') : i18n('common.expand');
  const resizeLabel = isExpanded ? i18n('common.shrinkModal') : i18n('common.expandModal');

  return (
    <>
      <HotKey hotkey={toggleFixedWidthHotkey} callback={onHotkey} />
      <Button
        ref={buttonRef}
        icon={icon}
        iconOnly
        iconGridSize={3.75}
        variant="ghost"
        onClick={onClick}
        testID={testID}
        className={className}
        label={resizeLabel}
        tooltip={resizeTooltip}
        tooltipPosition="bottom"
        tooltipHotkey={toggleFixedWidthHotkey}
      />
    </>
  );
}
